import { useEffect, useState, useRef, useCallback } from "react";
import { NodeSummary, useAddNode, useDeleteNode, useMergeNodes, useNodes } from "../utils/query";
import { Plus, Search } from "lucide-react";
import { AddNodeDialog } from "../components/AddNodeDialog";
import { ListNodeView } from "../components/ListNodeView";
import ErrorMessage from "../components/ErrorMessage";
import { generateNodes, sleep } from "../utils/utils";
import { useQueryClient } from "@tanstack/react-query";
import { ImportExportOptions } from "../components/ImportExportOptions";
import { ConfirmDeleteDialog } from "../components/ConfirmDeleteDialog";
import { useAuth } from "@clerk/clerk-react";

function Nodes() {
  const [searchTerm, setSearchTerm] = useState('');
  const [people, setPeople] = useState<NodeSummary[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const selectAllRef = useRef<HTMLInputElement>(null);
  const { getToken, isLoaded, isSignedIn } = useAuth()

  useEffect(() => {
    const fn = async () => {
      console.log("token", await getToken())
    }
    fn()
  }, [])

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
  } = useNodes({ searchParam: searchTerm });

  const queryClient = useQueryClient();
  const observer = useRef<IntersectionObserver>();

  const lastElementRef = useCallback((node: HTMLElement | null) => {
    if (isLoading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasNextPage) {
        fetchNextPage();
      }
    });

    if (node) observer.current.observe(node);
  }, [isLoading, fetchNextPage, hasNextPage]);

  useEffect(() => {
    if (data) {
      setPeople(prev => [...data.pages.flatMap(page => page.data)]);
    }
  }, [data]);

  useEffect(() => {
    if (selectAllRef.current) {
      // Indeterminate if some items are selected, but not all
      const selectedCount = selectedIds.size;
      selectAllRef.current.indeterminate = selectedCount > 0 && selectedCount < people.length;
    }
  }, [selectedIds, people]);

  const handleAddNode = () => {
    const modal = document.getElementById('add_node');
    if (modal) {
      (modal as HTMLDialogElement).showModal();
    }
  }

  const handleSelect = (id: string, isSelected: boolean) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  }

  const mergeNodesMutation = useMergeNodes();
  const deleteNodeMutation = useDeleteNode();


  const openDeleteDialog = () => {
    const modal = document.getElementById('confirm_delete_dialog');
    if (modal) {
      (modal as HTMLDialogElement).showModal();
    }
  }

  const handleDelete = async () => {
    console.log("Delete selected items:", Array.from(selectedIds));
    //TODO: Don't iterate over selectedIds, use a batch delete mutation
    for (const id of selectedIds) {
      deleteNodeMutation.mutate(id);
    }

    await sleep(100);
    await queryClient.invalidateQueries({ queryKey: ['nodes'] });

    setSelectedIds(new Set());
  }

  const handleMerge = () => {
    console.log("Merge selected items:", Array.from(selectedIds));
    mergeNodesMutation.mutate(Array.from(selectedIds));

  }

  const mutationAddNode = useAddNode();



  return (
    <div className="min-h-screen p-4 border p-1 max-w-3xl mx-auto">

      <AddNodeDialog />
      <ConfirmDeleteDialog
        message={`Do you really want to delete these nodes?`}
        onConfirm={function (): void {
          handleDelete();
          const modal = document.getElementById('confirm_delete_dialog');
          if (modal) {
            (modal as HTMLDialogElement).close();
          }
        }} onCancel={function (): void {
          const modal = document.getElementById('confirm_delete_dialog');
          if (modal) {
            (modal as HTMLDialogElement).close();
          }
        }} />
      <div className='sticky top-0 bg-base-100 z-10'>
        {/* Search Bar */}
        <div className="relative">
          <div className="flex justify-center mb-4 max-w-3xl mx-auto ">
            <button
              className="btn mr-2"
              onClick={handleAddNode}
              aria-label="Add Node"
            >
              <Plus />
            </button>
            <label className="input input-bordered flex items-center gap-2 w-full">
              <input
                type="text"
                className="grow"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search />
            </label>
          </div>
        </div>
        <div className="flex justify-start border-b pb-4 max-w-3xl mx-auto space-x-4 items-center">
          <input
            ref={selectAllRef}
            type="checkbox"
            className="checkbox checkbox-sm ml-3 mt-2 mb-2"
            aria-label="Select All"
          />
          <>
            <button className="btn btn-sm" onClick={openDeleteDialog} disabled={selectedIds.size === 0}>Delete</button>
            <button className="btn btn-sm" onClick={handleMerge} disabled={selectedIds.size !== 2}>Merge</button>
          </>
        </div>
      </div>

      <div className="max-w-3xl mx-auto">
        {isLoading && people.length === 0 ? (
          <div className="w-full text-center">
            <span className="loading loading-dots loading-lg"></span>
          </div>
        ) : isError ? (
          <ErrorMessage message="Unable to load Nodes" />
        ) : people.length === 0 && searchTerm.trim() === "" ? (
          <ImportExportOptions
            onAddOne={handleAddNode}
            onGenerateRandom={async () => {
              const nodes = generateNodes(1000);
              for (const node of nodes) {
                await mutationAddNode.mutateAsync(node);
              }
              await sleep(100);
              queryClient.invalidateQueries({ queryKey: ['nodes'] });
            }}
          />
        ) : people.length === 0 ? (
          <div className="text-center text-gray-500">No results found.</div>
        ) : (
          <>
            <div className="max-w-3xl mx-auto">
              {people.map((person, index) => {
                if (people.length === index + 1) {
                  return (
                    <div ref={lastElementRef} key={person.id}>
                      <ListNodeView
                        person={person}
                        isSelected={selectedIds.has(person.id)}
                        onSelect={handleSelect}
                      />
                    </div>
                  );
                }
                return (
                  <ListNodeView
                    key={person.id}
                    person={person}
                    isSelected={selectedIds.has(person.id)}
                    onSelect={handleSelect}
                  />
                );
              })}
            </div>
            {isLoading && hasNextPage && (
              <div className="w-full text-center mt-4">
                <span className="loading loading-dots loading-lg"></span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Nodes
